import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { checkRoleLevel, getLocalMemberRole, ROLE } from '../members';
import ChatBodyContainer from './ChatBodyContainer';
import ChatInputContainer from './ChatInputContainer';
import ChatNotificationContainer from './ChatNotificationContainer';
import { AddFile } from './components';
import { setIsKeyboardOpen } from './action';
import { isIpadLikeBrowser, KEYBOARD_CLOSED_DIFF, resetStaleScrollTops } from './staleScrollReset';
const Chat = React.memo(({ isMonitoring }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMimac = APP.config?.name === 'mimac';
    const smallMobile = window.innerWidth <= 505 || window.innerHeight <= 505;
    const chatRef = useRef(null);
    // ── iPad(가로) 오버레이 키보드 대응: 사이드바 스코프 스페이서 ──
    // 키보드(오버레이, 실측 diff≈493)가 채팅 하단을 덮을 때 .chat-container 마지막
    // 자식으로 스페이서를 넣어 .chat_content(flex:1)만 압축 → 입력 래퍼·최근 메시지가
    // 키보드 위 가시영역으로 올라온다. 전역 기하(#root/.body/.layout 의
    // height/transform/--vh)는 일절 건드리지 않는다(과거 실패 5종 전면 금지).
    // 스페이서는 컨테이너 "내부"라 컨테이너 rect 가 불변 → 측정→적용 피드백 루프 없음.
    const containerRef = useRef(null);
    const inputWrapperRef = useRef(null);
    const [spacerH, setSpacerH] = useState(0);
    useEffect(() => {
        // 게이트: iPad 계열 && 비폰. screen 기반 판정(ChatInputContainer/Room.web.tsx 와
        // 동일 기준) — 위의 window.inner* 기반 smallMobile 과 혼용 금지(회전·키보드로 변동).
        const smallMobileScreen = screen.width <= 505 || screen.height <= 505;
        if (smallMobileScreen || !isIpadLikeBrowser())
            return;
        if (!window.visualViewport)
            return;
        let debounceTimeout = null;
        const apply = () => {
            const vv = window.visualViewport;
            if (!vv || !containerRef.current)
                return;
            const diff = window.innerHeight - vv.height;
            // 닫힘 판정(diff < 120): 오토필 바 79px 고착 포함 → 스페이서 제거.
            if (diff < KEYBOARD_CLOSED_DIFF) {
                setSpacerH(0);
                return;
            }
            // 포커스 게이트: 채팅 textarea 가 아닌 입력(테스트 주관식 등)으로 뜬
            // 키보드에 오발화 방지 — 채팅 컨테이너 내부 TEXTAREA 포커스일 때만 동작.
            const active = document.activeElement;
            if (!active || active.tagName !== 'TEXTAREA' || !containerRef.current.contains(active)) {
                setSpacerH(0);
                return;
            }
            // ★ 측정 전에 UA(브라우저)가 포커스 reveal 로 overflow:hidden 조상들
            //   (html/#root/#room-container/.body/.layout)에 밀어넣은 scrollTop 을 0 으로 원복한다.
            //   이유 2가지:
            //   (1) 이 조상 scrollTop 이 사용자가 보는 "전체 페이지 스크롤"의 정체 — 닫힘뿐 아니라
            //       열림 중에도 제거해야 한다(과거엔 닫힘에서만 리셋해 열림 중 스크롤이 잔존).
            //   (2) 원복 전에는 rect.bottom 이 스크롤만큼 작아져 occluded 가 0/음수로 수렴 →
            //       스페이서가 삽입되지 않아 입력창·최근 메시지가 안 올라온다(현재 증상).
            //       원복 후의 "참(true) rect" 로 측정해야 정확한 리프트량이 나온다.
            //   스페이서가 곧바로 입력을 가시영역으로 올리므로 UA 가 재-reveal 할 유인이 없어 수렴한다.
            //   (overflow:auto/scroll 인 채팅 리스트는 util 이 보존하므로 스크롤 위치 무영향)
            resetStaleScrollTops(active);
            // 위 scrollTop 쓰기 반영을 위해 강제 리플로우 후 참 rect 측정. offsetTop 은 실측 0 이나 방어 포함.
            const rect = containerRef.current.getBoundingClientRect();
            const occluded = Math.round(rect.bottom - (vv.offsetTop + vv.height));
            // 클램프: 입력 래퍼(.chat_input_wrapper) 완전 가시 보장 상한.
            // (첫 적용 땐 chat_head 가 아직 보여 reserve 가 크지만, 아래 지연 재적용에서 head 숨김 후
            //  더 작은 reserve 로 재측정되어 스페이서가 정확히 커진다.)
            const reserve = inputWrapperRef.current?.offsetHeight ?? 0;
            const max = Math.max(0, Math.round(rect.height - reserve));
            setSpacerH(Math.min(Math.max(occluded, 0), max));
        };
        const handleVisualViewportResize = () => {
            // 키보드 애니메이션과 싸우지 않기 — 250ms 정착 후 적용(Room.web.tsx 트리거① 동형).
            // 늦은 UA 재-reveal(스크롤 되밀기)·chat_head 숨김 후 reserve 재측정을 위해 +300ms 재적용(멱등).
            if (debounceTimeout)
                clearTimeout(debounceTimeout);
            debounceTimeout = window.setTimeout(() => {
                apply();
                window.setTimeout(apply, 300);
            }, 250);
        };
        window.visualViewport.addEventListener('resize', handleVisualViewportResize);
        // 키보드가 이미 열린 채 리마운트(채팅 접었다 펼침 등)된 경우 대비 1회 적용.
        apply();
        return () => {
            window.visualViewport?.removeEventListener('resize', handleVisualViewportResize);
            if (debounceTimeout)
                clearTimeout(debounceTimeout);
        };
    }, []);
    const isShowLvl = useSelector((state) => checkRoleLevel(getLocalMemberRole(state), ROLE.PRESENTER));
    const enableChatView = useSelector((state) => state['features/base/settings'].policy.notification_option.enable_chat_view);
    const showSendChat = useMemo(() => !isMonitoring && (enableChatView || isShowLvl), [isMonitoring, enableChatView, isShowLvl]);
    useEffect(() => {
        if (!showSendChat)
            dispatch(setIsKeyboardOpen(false));
    }, [showSendChat]);
    useEffect(() => {
        const node = chatRef.current;
        if (!node)
            return;
        const stopPropagation = (e) => e.stopPropagation();
        node.addEventListener('touchmove', stopPropagation, { passive: false });
        node.addEventListener('wheel', stopPropagation, { passive: false });
        node.addEventListener('scroll', stopPropagation, { passive: false });
        return () => {
            node.removeEventListener('touchmove', stopPropagation);
            node.removeEventListener('wheel', stopPropagation);
            node.removeEventListener('scroll', stopPropagation);
        };
    }, []);
    return (React.createElement("div", { className: "chat-container", ref: containerRef },
        React.createElement(ChatNotificationContainer, null),
        React.createElement("div", { className: "chat_content", style: { position: 'relative' }, ref: chatRef },
            React.createElement(ChatBodyContainer, { showSendChat: showSendChat })),
        showSendChat && (React.createElement("div", { className: "chat_input_wrapper", ref: inputWrapperRef },
            React.createElement("div", { className: "chat_head", style: { display: (isMimac && smallMobile && !isShowLvl) || spacerH > 0 ? 'none' : 'flex' } },
                React.createElement("span", { className: "title" }, t('chat.inputLabel')),
                React.createElement("div", { className: "btn_left_wrapper" }, APP.config.option.hideParticipant && isShowLvl && React.createElement(AddFile, null))),
            React.createElement(ChatInputContainer, null))),
        spacerH > 0 && React.createElement("div", { className: "chat_keyboard_spacer", style: { height: spacerH, flexShrink: 0 } })));
});
export default Chat;
